/**
 * QA Panel — postMessage protocol between the lobby and the popup window.
 *
 * Every message carries a `namespace` discriminator so the bridge can ignore
 * unrelated traffic (the lobby uses postMessage with the Game Client too).
 */

import { MESSAGE_NAMESPACE } from './constants';
import type { BackofficeState, GameEditPatch } from './backoffice/backofficeController';
import type { JackpotMockState } from './jackpot/jackpotMockController';
import type { PresetImportResult, PresetState, QaPreset } from './preset/presetController';
import type { SentryDebugState } from './sentry/sentryController';
import type { JiraTabState } from './jira/jiraController';
import type {
    CampaignsState,
    CreateMockTournamentForm,
    FetchExternalResult,
} from './campaigns/campaignsController';
import type {
    CreateMockPrizePopForm,
    PrizePopState,
} from './campaigns/prizePopController';
import type {
    CreateMockRebateForm,
    RebateState,
} from './campaigns/rebateController';
import type { ITournament, IPrizePopCampaign, IRebateCampaign } from '../campaigns';
import type { ConsoleState } from './console/consoleController';
import type { JackpotWidgetConfig, JackpotWidgetState } from './jackpotWidget/jackpotWidgetController';
import type { EnvironmentState } from './environment/environmentController';
import type { MeasureRuler, MeasureSettings, MeasureState } from './measure/measureController';
import type { MeasureAxis } from './measure/units';
import type { LanguageInspection, QaMode, QaPanelSnapshot, ThumbnailCheckResult } from './types';

export type { SentryDebugGameLaunch, SentryDebugState } from './sentry/sentryController';

export interface BaseMessage {
  namespace: typeof MESSAGE_NAMESPACE;
}

/* ── Lobby → Popup ──────────────────────────────────────────────── */

export type LobbyToPopupMessage = BaseMessage &
  /** Initial / refreshed snapshot of panel state. */
  (| { type: 'state'; payload: QaPanelSnapshot }
    /** A single missing key was just observed (incremental update). */
    | { type: 'missingKey'; payload: { lang: string; key: string } }
    /** Triggered when the user clicked "Export missing keys"; popup downloads. */
    | { type: 'exportReady'; payload: { csv: string; filename: string } }
    /** Reply to an `inspectLanguage` request — full per-key analysis for one lang. */
    | { type: 'inspection'; payload: LanguageInspection }
    /** Jackpot mock state snapshot, sent on every state-affecting change. */
    | { type: 'jackpot.state'; payload: JackpotMockState }
    /** Outcome of a `jackpot.generateWinStats` request — `rows` is -1 when nothing was generated. */
    | { type: 'jackpot.winStatsGenerated'; payload: { jackpotName: string; rows: number } }
    /** Mini Backoffice state snapshot. */
    | { type: 'backoffice.state'; payload: BackofficeState }
    /** Results of a thumbnail availability check. */
    | { type: 'backoffice.thumbnailResults'; payload: ThumbnailCheckResult }
    /* ── Preset ───────────────────────────────────────────── */
    /** Preset state snapshot (auto-save toggle, stored preset info). */
    | { type: 'preset.state'; payload: PresetState }
    /** Export-ready JSON + suggested filename for download. */
    | { type: 'preset.exportReady'; payload: { json: string; filename: string } }
    /** Result of an import operation. */
    | { type: 'preset.importResult'; payload: PresetImportResult }
    /* ── Sentry debug ─────────────────────────────────────── */
    /** Polled snapshot of every tracker singleton's current data. */
    | { type: 'sentry.state'; payload: SentryDebugState }
    /* ── Jira ─────────────────────────────────────────────── */
    /** Build-time list of Jira tickets included in this build. Static. */
    | { type: 'jira.state'; payload: JiraTabState }
    /* ── Campaigns (Tournaments mock) ─────────────────────── */
    /** Campaigns mock state snapshot — list of mocked tournaments + flag. */
    | { type: 'campaigns.state'; payload: CampaignsState }
    /** Confirmation that a mock tournament was created. */
    | { type: 'campaigns.created'; payload: { id: string; name: string } }
    /** Result of an external fetch driven by user-pasted creds. */
    | { type: 'campaigns.fetchResult'; payload: FetchExternalResult }
    /* ── Campaigns (PrizePop mock) ────────────────────────── */
    /** PrizePop mock state snapshot — list of mocked campaigns + flag. */
    | { type: 'prizepop.state'; payload: PrizePopState }
    /** Confirmation that a mock PrizePop campaign was created. */
    | { type: 'prizepop.created'; payload: { id: string; name: string } }
    /** Result of an external PrizePop fetch driven by user-pasted creds. */
    | { type: 'prizepop.fetchResult'; payload: FetchExternalResult }
    /* ── Campaigns (Rebate mock) ──────────────────────────── */
    /** Rebate mock state snapshot — list of mocked campaigns + flag. */
    | { type: 'rebate.state'; payload: RebateState }
    /** Confirmation that a mock Rebate campaign was created. */
    | { type: 'rebate.created'; payload: { id: string; name: string } }
    /** Result of an external Rebate fetch driven by user-pasted creds. */
    | { type: 'rebate.fetchResult'; payload: FetchExternalResult }
    /* ── Console ──────────────────────────────────────────── */
    /** Captured-errors snapshot. Sent on every state-affecting change (debounced). */
    | { type: 'console.state'; payload: ConsoleState }
    /* ── Environment ──────────────────────────────────────── */
    /** Static catalog of casinos + current-session prefill data. */
    | { type: 'environment.state'; payload: EnvironmentState }
    /* ── Jackpot widget injector ──────────────────────────── */
    /** Injector state — form config, load status, and the loaded bundle's lists. */
    | { type: 'jackpotWidget.state'; payload: JackpotWidgetState }
    /* ── Measure (ruler + guides) ─────────────────────────── */
    /** Ruler / guides / unit snapshot. Sent on every state-affecting change. */
    | { type: 'measure.state'; payload: MeasureState }
    /* ── Figma overlay (tab disabled — re-enable by uncommenting) ── */
    // | { type: 'figma.state'; payload: FigmaState }
    // | { type: 'figma.image'; payload: { image: string | null } }
  );

/* ── Popup → Lobby ──────────────────────────────────────────────── */

export type PopupToLobbyMessage = BaseMessage &
  /** Popup just loaded; lobby should send a fresh state snapshot. */
  (| { type: 'requestState' }
    /** Switch the lobby language. */
    | { type: 'switchLanguage'; payload: { lang: string } }
    /** Toggle the post-processor mode (normal / pseudo-loc / show-keys). */
    | { type: 'setMode'; payload: { mode: QaMode } }
    /** Preload all supportedLanguages into i18next so coverage matrix is complete. */
    | { type: 'preloadAllLanguages' }
    /** Clear the running missing-keys list. */
    | { type: 'clearMissingKeys' }
    /** Request a CSV export of the missing keys; lobby replies with `exportReady`. */
    | { type: 'exportMissingKeys' }
    /** Open the inspector for a specific language; lobby replies with `inspection`. */
    | { type: 'inspectLanguage'; payload: { lang: string } }
    /* ── Jackpot mock ──────────────────────────────────────── */
    /** Toggle mock mode on/off. ON stops real polling. */
    | { type: 'jackpot.setMockMode'; payload: { enabled: boolean } }
    /** Start an auto-trigger loop that fires every `intervalSec` seconds. */
    | { type: 'jackpot.startAuto'; payload: { intervalSec: number; percent: number } }
    /** Stop the auto-trigger loop. */
    | { type: 'jackpot.stopAuto' }
    /** Ask the lobby for a fresh jackpot mock state snapshot. */
    | { type: 'jackpot.requestState' }
    /** Manually set one jackpot level's value across every currency. */
    | { type: 'jackpot.setLevelValue'; payload: { jackpotName: string; levelId: number; value: number } }
    /** Inject (or update) a synthetic InstantLink instance. Auto-enables mock mode. */
    | { type: 'jackpot.injectInstant'; payload: { value: number } }
    /**
     * Toggle win-statistics mocking. ON makes the statistics drawer serve generated rows and
     * disables the real `/api/jackpot/win/stats` query; OFF drops every generated mock and
     * restores the poll counters the generator overwrote.
     */
    | { type: 'jackpot.setWinsMockMode'; payload: { enabled: boolean } }
    /**
     * Generate synthetic win statistics for one jackpot. Auto-enables jackpot mock mode AND
     * win-statistics mocking, so one click produces a populated drawer.
     */
    | {
          type: 'jackpot.generateWinStats';
          payload: { jackpotName: string; winsPerLevel: number; includeZeroWinLevel: boolean };
      }
    /** Drop generated win statistics for one jackpot, or for all of them when `jackpotName` is null. */
    | { type: 'jackpot.clearWinStats'; payload: { jackpotName: string | null } }
    /* ── Mini Backoffice ───────────────────────────────────── */
    /** Ask for a fresh backoffice state snapshot. */
    | { type: 'backoffice.requestState' }
    | { type: 'backoffice.renameSection'; payload: { oldName: string; newName: string } }
    | { type: 'backoffice.deleteSection'; payload: { name: string } }
    | { type: 'backoffice.addGame'; payload: { sectionName: string; gameKey: string } }
    | { type: 'backoffice.removeGame'; payload: { sectionName: string; gameKey: string } }
    | { type: 'backoffice.setSectionOrder'; payload: { names: string[] } }
    | { type: 'backoffice.setGameOrder'; payload: { sectionName: string; gameKeys: string[] } }
    | { type: 'backoffice.updateGame'; payload: { gameKey: string; patch: GameEditPatch } }
    | { type: 'backoffice.resetToApi' }
    | { type: 'backoffice.setOpenType'; payload: { openType: 'fullscreen' | 'ingame' } }
    | { type: 'backoffice.setSeasonalBranding'; payload: { value: string | null } }
    | { type: 'backoffice.setNoJackpot'; payload: { noJackpot: boolean } }
    | { type: 'backoffice.setCurrency'; payload: { currency: string } }
    | { type: 'backoffice.setHotColdMock'; payload: { enabled: boolean } }
    /** Toggle one of the campaign feature flags (PrizePop / Tournaments / Rebate). */
    | {
          type: 'backoffice.setCampaignFlag';
          payload: { flag: 'enablePrizePopCampaign' | 'enableTournamentsCampaign' | 'enableRebateCampaign'; value: boolean };
      }
    /** Request a thumbnail availability check for all games. */
    | { type: 'backoffice.checkThumbnails' }
    /* ── Preset ───────────────────────────────────────────── */
    /** Request export of the current panel state as a JSON preset. */
    | { type: 'preset.requestExport' }
    /** Import a previously-exported preset. */
    | { type: 'preset.import'; payload: { preset: QaPreset } }
    /** Toggle auto-save to localStorage. */
    | { type: 'preset.setAutoSave'; payload: { enabled: boolean } }
    /** Clear all stored preset data from localStorage. */
    | { type: 'preset.clearStorage' }
    /** Ask for a fresh preset state snapshot. */
    | { type: 'preset.requestState' }
    /* ── Sentry debug ─────────────────────────────────────── */
    /** Wipe every tracker singleton + the staged search-query buffer. */
    | { type: 'sentry.clearAll' }
    /** Force-attach the current tracker data onto the active Sentry span. */
    | { type: 'sentry.syncToSentry' }
    /* ── Jira ─────────────────────────────────────────────── */
    /** Ask for a fresh Jira tab state snapshot. Read-only / build-time data. */
    | { type: 'jira.requestState' }
    /* ── Console ──────────────────────────────────────────── */
    /** Ask for a fresh captured-errors snapshot. */
    | { type: 'console.requestState' }
    /** Wipe the captured-errors buffer. */
    | { type: 'console.clear' }
    /* ── Environment ──────────────────────────────────────── */
    /** Ask for the environment tab snapshot — static casino list + prefill data. */
    | { type: 'environment.requestState' }
    /* ── Jackpot widget injector ──────────────────────────── */
    /** Ask for the current injector state (form values + load status). */
    | { type: 'jackpotWidget.requestState' }
    /** Update one or more form fields without loading anything. */
    | { type: 'jackpotWidget.setConfig'; payload: { patch: Partial<JackpotWidgetConfig> } }
    /** Hand over the bytes of a picked zip; the lobby unpacks it in-browser. */
    | { type: 'jackpotWidget.loadZip'; payload: { name: string; bytes: ArrayBuffer } }
    /** Mount the section from the already-loaded zip onto the lobby page. */
    | { type: 'jackpotWidget.apply' }
    /** Take the section off the page. Not a teardown — see the controller. */
    | { type: 'jackpotWidget.disable' }
    /* ── Measure (ruler + guides) ─────────────────────────── */
    /** Ask for a fresh measure state snapshot. */
    | { type: 'measure.requestState' }
    /** Toggles (overlay, ruler, snap, highlight) and the display unit. */
    | { type: 'measure.set'; payload: { change: MeasureSettings } }
    /** Arm pick mode: the next click / tap on the lobby wraps that element in guides. */
    | { type: 'measure.startPick' }
    /** Patch ruler origin / length / orientation (px). */
    | { type: 'measure.setRuler'; payload: { patch: Partial<MeasureRuler> } }
    /** Ruler back to the default size and position. */
    | { type: 'measure.resetRuler' }
    /** Add a guide at the viewport center on `axis` (`x` = vertical line). */
    | { type: 'measure.addGuide'; payload: { axis: MeasureAxis } }
    | { type: 'measure.setGuide'; payload: { id: string; pos: number } }
    | { type: 'measure.removeGuide'; payload: { id: string } }
    | { type: 'measure.clearGuides' }
    /** Delete the saved measure state from localStorage and reset to the defaults. */
    | { type: 'measure.clearSaved' }
    /* ── Figma overlay (tab disabled — re-enable by uncommenting) ── */
    // | { type: 'figma.requestState' }
    // | { type: 'figma.requestImage' }
    // | { type: 'figma.setImage'; payload: { image: string | null; naturalWidth: number; naturalHeight: number } }
    // | { type: 'figma.setOverlayOn'; payload: { on: boolean } }
    // | { type: 'figma.setOpacity'; payload: { opacity: number } }
    // | { type: 'figma.setPosition'; payload: { x: number; y: number } }
    // | { type: 'figma.setScale'; payload: { scale: number } }
    // | { type: 'figma.resetView' }
    /* ── Campaigns (Tournaments mock) ─────────────────────── */
    /** Ask for a fresh campaigns state snapshot. */
    | { type: 'campaigns.requestState' }
    /** Flip mockMode on without seeding any data. */
    | { type: 'campaigns.enableMocking' }
    /** Clear every mock + flip mockMode off. */
    | { type: 'campaigns.disableMocking' }
    /** One-shot external fetch using QA-pasted creds. Replies with `campaigns.fetchResult`. */
    | { type: 'campaigns.fetchExternal'; payload: { sessionKey: string; playerId: string } }
    /** Build a synthetic tournament from form data and inject it. */
    | { type: 'campaigns.createMockTournament'; payload: { form: CreateMockTournamentForm } }
    /** Remove a single mocked tournament by id. */
    | { type: 'campaigns.removeMockTournament'; payload: { id: string } }
    /** Patch a single tournament in place (real or mocked). */
    | { type: 'campaigns.updateTournament'; payload: { id: string; patch: Partial<ITournament> } }
    /** Clear every mock and resume real polling. */
    | { type: 'campaigns.clearAll' }
    /* ── Campaigns (PrizePop mock) ────────────────────────── */
    /** Ask for a fresh PrizePop state snapshot. */
    | { type: 'prizepop.requestState' }
    /** Flip mockMode on without seeding any data. */
    | { type: 'prizepop.enableMocking' }
    /** Clear every mock + flip mockMode off. */
    | { type: 'prizepop.disableMocking' }
    /** One-shot external fetch using QA-pasted creds. Replies with `prizepop.fetchResult`. */
    | { type: 'prizepop.fetchExternal'; payload: { sessionKey: string; playerId: string } }
    /** Build a synthetic PrizePop campaign from form data and inject it. */
    | { type: 'prizepop.createMockCampaign'; payload: { form: CreateMockPrizePopForm } }
    /** Remove a single mocked campaign by id. */
    | { type: 'prizepop.removeMockCampaign'; payload: { id: string } }
    /** Patch a single PrizePop campaign in place (real or mocked). */
    | { type: 'prizepop.updateCampaign'; payload: { id: string; patch: Partial<IPrizePopCampaign> } }
    /** Clear every mock and resume real polling. */
    | { type: 'prizepop.clearAll' }
    /* ── Campaigns (Rebate mock) ──────────────────────────── */
    /** Ask for a fresh Rebate state snapshot. */
    | { type: 'rebate.requestState' }
    /** Flip mockMode on without seeding any data. */
    | { type: 'rebate.enableMocking' }
    /** Clear every mock + flip mockMode off. */
    | { type: 'rebate.disableMocking' }
    /** One-shot external fetch using QA-pasted creds. Replies with `rebate.fetchResult`. */
    | { type: 'rebate.fetchExternal'; payload: { sessionKey: string; playerId: string } }
    /** Build a synthetic Rebate campaign from form data and inject it. */
    | { type: 'rebate.createMockCampaign'; payload: { form: CreateMockRebateForm } }
    /** Remove a single mocked campaign by id. */
    | { type: 'rebate.removeMockCampaign'; payload: { id: string } }
    /** Patch a single Rebate campaign in place (real or mocked). */
    | { type: 'rebate.updateCampaign'; payload: { id: string; patch: Partial<IRebateCampaign> } }
    /** Clear every mock and resume real polling. */
    | { type: 'rebate.clearAll' }
  );

/* ── Type guards ─────────────────────────────────────────────────── */

export function isQaPanelMessage<T extends BaseMessage>(data: unknown): data is T {
  return typeof data === 'object' && data !== null && (data as { namespace?: unknown }).namespace === MESSAGE_NAMESPACE;
}
